import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { homePath } from '../../layout/navConfig';
import { Icon, Logo, MicrosoftLogo } from '../../design/Icon';
import { ThemeToggle } from '../../components/shared/Button';
import { LOGIN_COPY as COPY, LOGIN_EXAMPLE_COMMUNITY, LOGIN_ROUTE_STEPS as ROUTE_STEPS } from './loginCopy';
import './SignInPage.css';

function SignInError({ signedIn, error, onRetry, onSignOut }) {
  if (!error) return null;
  const disabled = /disabled/i.test(error);
  const title = signedIn
    ? disabled
      ? 'Your Vertolytics access is turned off'
      : "Vertolytics couldn't load your profile"
    : "Sign-in didn't finish";
  return (
    <div className="alert alert-error" role="alert">
      <Icon name="alert" />
      <div className="stack" style={{ gap: 6 }}>
        <div className="a-title">{title}</div>
        <div>{error}</div>
        {signedIn && (
          <div className="row">
            {!disabled && (
              <button className="btn btn-sm" type="button" onClick={onRetry}>
                Try again
              </button>
            )}
            <button className="btn btn-sm" type="button" onClick={onSignOut}>
              Try a different account
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export function SignInPage() {
  const { login, logout, retryProfile, isAuthenticated, signedIn, loading, error, user, hasPermission } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [redirecting, setRedirecting] = useState(false);

  useEffect(() => {
    document.title = 'Sign in | Vertolytics';
  }, []);

  useEffect(() => {
    if (isAuthenticated && !loading) {
      navigate(user?.needsOnboarding ? '/onboarding' : location.state?.from || homePath(hasPermission), { replace: true });
    }
  }, [isAuthenticated, loading, navigate, user, location.state, hasPermission]);

  const startSignIn = async () => {
    setRedirecting(true);
    const result = await login();
    if (!result?.success) setRedirecting(false);
  };

  const busy = redirecting || (loading && !error);

  return (
    <div className="signin">
      <section className="signin-brand" aria-label="About Vertolytics">
        <div className="logo">
          <span>
            <Logo size={30} />
          </span>
          Vertolytics
        </div>
        <h1>{COPY.headline}</h1>
        <p>{COPY.intro}</p>
        <div className="lineage-demo" aria-label="Example call route">
          <div className="ld-head">
            <span>Example call route</span>
            <span className="num">Today 9:41 AM · 6:52 total</span>
          </div>
          <div className="ld-track">
            <div className="ld-fill" aria-hidden="true" />
            {ROUTE_STEPS.map((step) => (
              <div className="ld-step" style={{ color: step.color }} key={step.kind}>
                <span className="n" />
                <div className="k">{step.kind}</div>
                <div className="v">{step.name}</div>
                <div className="d">{step.detail}</div>
              </div>
            ))}
          </div>
          <div className="ld-ai">
            <span>Classified as</span>
            <span className="tag">Gate and Access Control › Transponder or Remote</span>
            <span className="tag">{LOGIN_EXAMPLE_COMMUNITY}</span>
          </div>
        </div>
        <div className="foot">
          <span>© {new Date().getFullYear()} Anadak LLC</span>
          <a href="#privacy" style={{ color: 'inherit' }}>
            Privacy
          </a>
          <a href="#terms" style={{ color: 'inherit' }}>
            Terms
          </a>
          <a href="#status" style={{ color: 'inherit' }}>
            Status
          </a>
        </div>
      </section>

      <main className="signin-main">
        <div className="signin-top">
          <ThemeToggle withLabel />
        </div>
        <div className="signin-card">
          <div>
            <h2>Sign in</h2>
            <p className="sub" style={{ marginTop: 6 }}>
              {COPY.account}
            </p>
          </div>

          <SignInError signedIn={signedIn} error={error} onRetry={retryProfile} onSignOut={logout} />

          {!signedIn &&
            (busy ? (
              <div className="stack" style={{ gap: 12 }} aria-live="polite">
                <button className="ms-btn" type="button" disabled>
                  <span className="spinner" aria-hidden="true" />
                  Opening Microsoft sign-in…
                </button>
                <p className="fine">
                  If nothing happens in a few seconds,{' '}
                  <a
                    href="#sign-in"
                    onClick={(e) => {
                      e.preventDefault();
                      startSignIn();
                    }}
                  >
                    open sign-in again
                  </a>
                  .
                </p>
              </div>
            ) : (
              <div className="stack" style={{ gap: 12 }}>
                <button className="ms-btn" type="button" onClick={startSignIn}>
                  <span>
                    <MicrosoftLogo size={18} />
                  </span>
                  Sign in with Microsoft
                </button>
                <p className="fine">You'll be sent to Microsoft to confirm it's you. Vertolytics never sees your password.</p>
              </div>
            ))}

          <div style={{ borderTop: '1px solid var(--divider)', paddingTop: 16 }} className="fine">
            Setting up Vertolytics for a new organization? <Link to="/onboarding">Connect your Microsoft 365 tenant</Link>. You'll need a Global Administrator or Privileged
            Role Administrator to approve read-only access.
          </div>
        </div>
      </main>
    </div>
  );
}
